"use client";

import { useState, useMemo, useCallback } from "react";
import { twMerge } from "tailwind-merge";
import { Icon, IconName } from "../icons/Icon";
import { colors, spacing } from "~/utils/tailwindUtils";
import Flex from "../flex/Flex";
import { useCurrentLocale } from "~/locales/client";
import { LocaleEnum } from "~/locales/resources";

export interface NumberInputProps {
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isInvalid?: boolean;
  errorMessage?: string;
  required?: boolean;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  value?: string;
  name?: string;
  placeholder?: string;
  classNames?: string;
  isDisabled?: boolean;
  verified?: boolean;
  loading?: boolean;
  autoComplete?: string;
  min?: number;
  max?: number;
  step?: number;
  /**
   * When true, the fractional part of the number is preserved verbatim
   * (including trailing zeros and the mid-typing state). The integer
   * part is still formatted with thousand separators. Opt-in to avoid
   * affecting callers that depend on the existing parseFloat-roundtrip
   * normalization. See TT-1360.
   */
  preserveTrailingZeros?: boolean;
}

/**
 * NumberInput component that allows the user to input only numeric values
 * @param name
 * @param placeholder
 * @param onChange
 * @param onBlur
 * @param value
 * @param isDisabled
 * @param classNames
 * @param verified
 * @param loading
 * @param min
 * @param max
 * @param step
 * @returns NumberInput component
 */
export const NumberInput = ({
  name,
  placeholder,
  onChange,
  onBlur,
  value,
  isDisabled,
  classNames,
  verified,
  loading,
  autoComplete,
  min,
  max,
  step,
  preserveTrailingZeros = false,
}: NumberInputProps) => {
  const locale = useCurrentLocale();
  const [userInputValue, setUserInputValue] = useState<string | null>(null);

  // Create Intl.NumberFormat instance for the current locale (memoized)
  const numberFormatter = useMemo(
    () =>
      new Intl.NumberFormat(locale === LocaleEnum.DE ? "de-DE" : "en-US", {
        style: "decimal",
        minimumFractionDigits: 0,
        maximumFractionDigits: 20, // Allow many decimal places
      }),
    [locale]
  );

  // Format number with thousand separators based on locale using Intl.NumberFormat
  const formatNumber = useCallback(
    (numStr: string): string => {
      if (!numStr || numStr === "" || numStr === "-") return numStr;

      // Remove all non-numeric characters except decimal point and minus sign
      const cleaned = numStr.replace(/[^\d.-]/g, "");
      if (cleaned === "" || cleaned === "-") return cleaned;

      // Parse to number
      const numValue = parseFloat(cleaned);
      if (isNaN(numValue)) return cleaned;

      // Format using Intl.NumberFormat
      let formatted = numberFormatter.format(numValue);

      // For German locale, Intl.NumberFormat uses comma as decimal separator
      // But we want to use dot as decimal separator to match the stored format
      // So we need to replace comma with dot for decimals
      if (locale === LocaleEnum.DE && cleaned.includes(".")) {
        // Check if there's a decimal part
        const parts = cleaned.split(".");
        if (parts.length === 2) {
          // Replace the comma decimal separator with dot
          formatted = formatted.replace(",", ".");
        }
      }

      // Handle negative sign
      if (cleaned.startsWith("-") && !formatted.startsWith("-")) {
        formatted = `-${formatted}`;
      }

      return formatted;
    },
    [numberFormatter, locale]
  );

  // Parse formatted number to raw numeric string (remove separators)
  const parseNumber = useCallback(
    (formattedStr: string): string => {
      if (!formattedStr || formattedStr === "") return "";

      // Get the locale-specific separators from Intl.NumberFormat
      const parts = numberFormatter.formatToParts(1234567.89);
      const thousandSeparator = parts.find((p) => p.type === "group")?.value || "";
      const decimalSeparator = parts.find((p) => p.type === "decimal")?.value || "";

      // Remove thousand separators
      let cleaned = formattedStr;
      if (thousandSeparator) {
        // Escape special regex characters
        const escapedSeparator = thousandSeparator.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        cleaned = cleaned.replace(new RegExp(escapedSeparator, "g"), "");
      }

      // Replace locale-specific decimal separator with dot (our standard)
      if (decimalSeparator && decimalSeparator !== ".") {
        cleaned = cleaned.replace(decimalSeparator, ".");
      }

      // Clean any remaining non-numeric characters except dot and minus
      cleaned = cleaned.replace(/[^\d.-]/g, "");

      return cleaned;
    },
    [numberFormatter]
  );

  // Locale-agnostic parser used when preserveTrailingZeros is on.
  // Treats the LAST `.` or `,` in the input as the decimal separator
  // (any earlier separators are dropped), so users can type either `.`
  // or `,` in any locale. Thousand separators aren't auto-inserted in
  // this mode (see formatPreservingFractional), so this also avoids the
  // ambiguity between auto-thousand-sep and user-typed decimal in DE
  // locale (where both are `.`). See TT-1360.
  const parseNumberSmart = useCallback((formattedStr: string): string => {
    if (!formattedStr) return "";
    let cleaned = formattedStr.replace(/[^\d.,-]/g, "");
    if (!cleaned) return "";
    const isNegative = cleaned.startsWith("-");
    if (isNegative) cleaned = cleaned.slice(1);
    const lastDotIdx = cleaned.lastIndexOf(".");
    const lastCommaIdx = cleaned.lastIndexOf(",");
    let decimalIdx = -1;
    if (lastDotIdx > lastCommaIdx) decimalIdx = lastDotIdx;
    else if (lastCommaIdx > lastDotIdx) decimalIdx = lastCommaIdx;
    if (decimalIdx === -1) {
      return `${isNegative ? "-" : ""}${cleaned.replace(/[.,]/g, "")}`;
    }
    const intPart = cleaned.slice(0, decimalIdx).replace(/[.,]/g, "");
    const fracPart = cleaned.slice(decimalIdx + 1).replace(/[.,]/g, "");
    return `${isNegative ? "-" : ""}${intPart}.${fracPart}`;
  }, []);

  // Format while keeping the fractional part exactly as the user typed
  // (preserves trailing zeros and partial decimals like "1.", "1.0", "1.05").
  // Decimal separator is locale-aware ("," in DE, "." in EN). Integer
  // part is left as raw digits (no thousand separators) to avoid
  // collision with the user-typed decimal separator while typing.
  // See TT-1360.
  const formatPreservingFractional = useCallback(
    (rawValue: string): string => {
      if (!rawValue) return "";
      if (rawValue === "-" || rawValue === ".") return rawValue;
      if (!rawValue.includes(".")) return rawValue;
      const dotIndex = rawValue.indexOf(".");
      const intPart = rawValue.slice(0, dotIndex);
      const fracPart = rawValue.slice(dotIndex + 1);
      const decimalSeparator =
        numberFormatter.formatToParts(1.1).find((p) => p.type === "decimal")?.value ?? ".";
      return `${intPart}${decimalSeparator}${fracPart}`;
    },
    [numberFormatter]
  );

  // Compute formatted value from prop
  const formattedPropValue = useMemo(() => {
    if (value !== undefined && value !== "") {
      return preserveTrailingZeros ? formatPreservingFractional(value) : formatNumber(value);
    }
    return "";
  }, [value, formatNumber, formatPreservingFractional, preserveTrailingZeros]);

  // Use user input if available, otherwise use formatted prop value
  const displayValue = userInputValue !== null ? userInputValue : formattedPropValue;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Parse the input to get raw numeric value
    const rawValue = preserveTrailingZeros ? parseNumberSmart(inputValue) : parseNumber(inputValue);

    // Validate that it's a valid number format
    // Allow: empty, minus sign, numbers, decimal point, and combinations like .2, .8, 0.5, 1.2, etc.
    // This regex allows:
    // - Empty string
    // - Just minus sign: "-"
    // - Just decimal point: "."
    // - Integers: "1", "10", "100"
    // - Decimals with leading zero: "0.8", "0.5", "0.2"
    // - Decimals without leading zero: ".8", ".5", ".2"
    // - Decimals with digits: "1.2", "12.34"
    // - Incomplete decimals: "0.", "1."
    // - Negative versions of all above
    const isValidFormat = inputValue === "" || 
      rawValue === "-" || 
      rawValue === "." ||
      /^-?(\d+\.?\d*|\.\d*)$/.test(rawValue);

    if (isValidFormat) {
      // For intermediate states like ".2", ".8", "0.", or incomplete decimals, preserve the raw input
      // This allows users to type ".2" and see ".2" instead of it being immediately converted to "0.2"
      // Also preserve values that start with "." followed by digits (e.g., ".2", ".8", ".12")
      const isIntermediateState = rawValue === "" || 
        rawValue === "-" || 
        rawValue.endsWith(".") || 
        (rawValue.startsWith(".") && /^\.\d+$/.test(rawValue));
      
      if (preserveTrailingZeros) {
        // Always route through formatPreservingFractional so intermediate
        // states (e.g. "189.", ".5") also use the locale decimal separator
        // and the integer part keeps thousand separators. See TT-1360.
        setUserInputValue(formatPreservingFractional(rawValue));
      } else if (isIntermediateState) {
        // Store the raw value for intermediate states (e.g., ".2", ".8", "0.", "-")
        setUserInputValue(rawValue);
      } else {
        // Store the formatted display value for complete numbers (e.g., "0.8", "1.2", "123")
        setUserInputValue(formatNumber(rawValue));
      }

      // Create a new event with the raw value
      if (onChange) {
        const syntheticEvent = {
          ...e,
          target: {
            ...e.target,
            value: rawValue,
          },
        } as React.ChangeEvent<HTMLInputElement>;
        onChange(syntheticEvent);
      }
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // If we have an intermediate state like ".8", normalize it to "0.8" on blur
    const currentValue = userInputValue !== null ? userInputValue : formattedPropValue;
    if (currentValue && (currentValue.startsWith(".") || currentValue.endsWith("."))) {
      const normalized = currentValue.startsWith(".") ? `0${currentValue}` : currentValue;
      const cleaned = normalized.replace(/\.$/, ""); // Remove trailing dot
      if (cleaned && !isNaN(parseFloat(cleaned))) {
        const formatted = formatNumber(cleaned);
        setUserInputValue(formatted);
      }
    }
    
    // Reset user input on blur so prop value takes precedence
    setUserInputValue(null);
    if (onBlur) {
      onBlur(e);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle Enter key - trigger blur to save the value
    if (e.keyCode === 13 || e.key === "Enter") {
      e.preventDefault();
      e.currentTarget.blur();
      return;
    }

    // Allow: backspace, delete, tab, escape, enter, decimal point, and minus sign.
    // When preserveTrailingZeros is set, also allow comma (188) so DE users can
    // type `,` as the decimal separator. See TT-1360.
    const allowedKeyCodes = preserveTrailingZeros
      ? [46, 8, 9, 27, 13, 110, 190, 189, 188]
      : [46, 8, 9, 27, 13, 110, 190, 189];
    if (
      allowedKeyCodes.indexOf(e.keyCode) !== -1 ||
      // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true) ||
      // Allow: home, end, left, right
      (e.keyCode >= 35 && e.keyCode <= 39)
    ) {
      return;
    }
    // Ensure that it is a number and stop the keypress
    if ((e.shiftKey || e.keyCode < 48 || e.keyCode > 57) && (e.keyCode < 96 || e.keyCode > 105)) {
      e.preventDefault();
    }
  };

  return (
    <Flex className="relative">
      <input
        name={name}
        id={name}
        type="text"
        inputMode="numeric"
        placeholder={placeholder}
        value={displayValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={isDisabled}
        autoComplete={autoComplete}
        min={min}
        max={max}
        step={step}
        className={twMerge(
          "bg-gray-50 hover:bg-gray-100 px-3 h-10 text-black border border-gray-300 rounded-lg text-xs md:text-sm font-medium w-full max-w-5xl focus:outline-none focus:border-gray-300 placeholder:text-xs",
          isDisabled && "opacity-50 text-gray-500 font-normal hover:bg-gray-50 cursor-not-allowed",
          classNames
        )}
      />
      {!loading && typeof verified === "boolean" ? (
        <Icon
          name={verified ? IconName.CheckCircle : IconName.CancelCircle}
          color={verified ? colors.green[500] : colors.red[500]}
          size={spacing[6]}
          className="absolute right-2 top-2 border-l border-gray-200 pl-2"
        />
      ) : loading ? (
        <Icon
          name={IconName.Loader}
          size={spacing[5]}
          color={colors.gray[500]}
          className="absolute right-2 top-3 animate-spin"
        />
      ) : null}
    </Flex>
  );
};
